import React, { createContext, useContext, useEffect, useState } from 'react';
import branch, { DeepLinkData } from 'branch-sdk';
import {
    BRANCH_KEY,
    BRANCH_KEY_AR,
    BRANCH_KEY_CE,
    BRANCH_KEY_MT,
} from '../configuration';

export interface IBranchProviderProps {
    children: React.ReactNode;
    data?: DeepLinkData;
    appId?: string;
}

interface IBranchContextValue {
    link?: string;
    generateReferralLink?: (
        shareToken: string,
        referralSource?: string
    ) => Promise<string>;
}

const BranchContext = createContext<IBranchContextValue>({});

/**
 * Since branch.link uses a callback, we first convert the function
 * into one that returns a promise
 */
export const generateBranchLink = (linkData: DeepLinkData) => {
    return new Promise<string>((resolve, reject) => {
        branch.link(linkData, (err, link) => {
            if (err) {
                return reject(err);
            } else if (link !== null) {
                return resolve(link);
            }
        });
    });
};

const BranchProvider = (props: IBranchProviderProps) => {
    const [shortLink, setShortLink] = useState<string | undefined>();

    const isCostEstimator = props.appId?.includes('costestimator');
    const isMileageTracker = props.appId?.includes('mileagetracker');
    const isVoiceAssistant = props.appId?.includes('voiceassistant');

    useEffect(() => {
        if (isCostEstimator && BRANCH_KEY_CE) {
            branch.init(BRANCH_KEY_CE);
        } else if (isMileageTracker && BRANCH_KEY_MT) {
            branch.init(BRANCH_KEY_MT);
        } else if (isVoiceAssistant && BRANCH_KEY_AR) {
            branch.init(BRANCH_KEY_AR);
        } else if (BRANCH_KEY) {
            branch.init(BRANCH_KEY);
        }
        /**
         * To match in-app events we report the the fbp, fbc, and userAgent
         * to the Conversions API
         */

        generateBranchLink(props.data || {})
            .then((link) => {
                if (process.env.NODE_ENV === 'development') {
                    console.debug('Set branch link');
                }
                setShortLink(link);
            })
            .catch((err) => {
                setShortLink(
                    isCostEstimator
                        ? 'https://swcostestimator.app.link'
                        : isMileageTracker
                        ? 'https://swmileagetracker.app.link'
                        : isVoiceAssistant
                        ? 'https://swvoiceassistant.app.link'
                        : 'https://simplywise.app.link'
                );
                console.error(err);
            });
    }, []);

    const generateReferralLink = async (
        shareToken: string,
        referralSource?: string
    ) => {
        const linkData: DeepLinkData = {
            data: {
                landing_page_id: referralSource || 'web-share',
                shareToken,
            },
        };
        return generateBranchLink(linkData);
    };

    return (
        <BranchContext.Provider
            value={{ link: shortLink, generateReferralLink }}
        >
            {props.children}
        </BranchContext.Provider>
    );
};

export default BranchProvider;

export const useBranch = () => useContext(BranchContext);
